// i18n: 認証前に実装される可能性があるため対応しない
import { getApolloContext } from '@apollo/client'
import { notifyBugsnagFromErrorBoundary } from '@salescore/client-base'
import { CardContainer, ErrorContext } from '@salescore/client-common'
import { Link, useNavigate } from '@tanstack/react-router'
import { Button, Result } from 'antd'
import { type ReactElement, useContext, useEffect } from 'react'
import JSONPretty from 'react-json-pretty'

const UnauthorizedError = ({ error }: { error: Error }): ReactElement => {
  const { clearErrors } = useContext(ErrorContext)
  const { client } = useContext(getApolloContext())
  const navigate = useNavigate()

  useEffect(() => {
    // 古いキャッシュを読み込み続けてエラーになるケースがあるため、キャッシュクリアする
    void client?.resetStore()

    if (error.message.includes(`この組織へのアクセス権限がありません`)) {
      // ネットワークのエラーはこのレイヤで通知せずErrorLinkで通知（そもそも設計の変更でここにはたどり着かないはず）
      // notifyBugsnag({ error, user: myUser, severity: 'info' })
      clearErrors()
      void navigate({ to: '/' })
    }

    if (
      error.message === 'トークンの有効期限が切れています' ||
      error.message.includes(`認証に失敗しました。再度ログインをお試しください。`)
    ) {
      // void message.error(`認証の有効期限が切れました。再ログインしてください`) // 何度も表示されるようになり、解決できなかったのでコメントアウト
      clearErrors()
      void navigate({ to: '/sign_out' })
    }
    if (error.message.includes(`存在しないアカウントです`)) {
      // ネットワークのエラーはこのレイヤで通知せずErrorLinkで通知（そもそも設計の変更でここにはたどり着かないはず）
      // notifyBugsnag({ error, user: myUser, severity: 'info' })
      clearErrors()
      void navigate({ to: '/sign_up' })
    }
  }, [clearErrors, client, error.message, navigate])

  return (
    <Result
      status="403"
      title="認証エラー"
      subTitle={error.message}
      extra={
        <div className="text-center">
          <Link to="/sign_out">
            <Button type="default" onClick={clearErrors}>
              ログイン画面へ
            </Button>
          </Link>
        </div>
      }
    />
  )
}

const NotFoundError = (): ReactElement => {
  const { clearErrors } = useContext(ErrorContext)
  useEffect(() => {
    // ネットワークのエラーはこのレイヤで通知せずErrorLinkで通知（そもそも設計の変更でここにはたどり着かないはず）
    // notifyBugsnag({ error, user: myUser, severity: 'info' })
  }, [])

  return (
    <Result
      status="404"
      title="404"
      subTitle="このページは存在しません。"
      extra={
        <div className="text-center">
          <Link to="/">
            <Button type="primary" onClick={clearErrors}>
              ホーム画面へ
            </Button>
          </Link>
          <br />
          <br />
          <Link to="/sign_out">
            <Button type="default" onClick={clearErrors}>
              ログイン画面へ
            </Button>
          </Link>
        </div>
      }
    />
  )
}

const StandardError = ({ errors }: { errors: Error[] }): ReactElement => {
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: clientを渡せておらず、使えない…
  // const [createActionLogMutation] = useCreateActionLogMutation()
  const { clearErrors } = useContext(ErrorContext)

  useEffect(() => {
    for (const error of errors) {
      notifyBugsnagFromErrorBoundary({ error })
    }
  }, [])

  return (
    <Result
      title="500"
      subTitle={
        <p>
          申し訳ありません、エラーが発生しました。
          <br />
          画面を再読み込みしてください。
          <br />
          エラーが続く場合は、右下のチャット画面よりお問い合わせください。
        </p>
      }
      className="text-gray-300"
      extra={
        <div className="text-center">
          <Link to="/">
            <Button
              type="primary"
              onClick={() => {
                location.reload()
              }}
            >
              画面を再読み込み
            </Button>
          </Link>
          <br />
          <br />
          <Link to="/sign_out">
            <Button type="default" onClick={clearErrors}>
              ログイン画面へ
            </Button>
          </Link>
        </div>
      }
    />
  )
}

const WaitingVerification = (): ReactElement => (
  <div className="flex size-full flex-col items-center justify-center text-center align-middle text-gray-400">
    <div>
      メールアドレスの認証待ちです。
      <br />
      登録したメールアドレスに認証メールをお送りしましたので、ご確認ください。
    </div>
    <div className="mt-4">
      <Link to="/sign_out">
        <Button type="primary">違うアカウントでログインする</Button>
      </Link>
    </div>
  </div>
)

const DevelopmentError = ({ errors }: { errors: Error[] }): ReactElement => (
  <div className="h-auto px-8 py-4" style={{ backgroundColor: '#272823' }}>
    <JSONPretty id="json-pretty" data={errors} style={{ whiteSpace: 'pre-wrap' }} />
  </div>
)

export const ErrorCard = ({ errors }: { errors: Error[] }): ReactElement => {
  const authorizationError = errors.find((x) => x.message.includes('Unauthorized') || x.message.includes('Forbidden'))
  const notFoundError = errors.find((x) => x.message.includes('Not Found') || x.message.includes('NotFound'))
  const waitingVerificationError = errors.find((x) => x.message.includes('メールアドレス認証が完了していません'))

  if (process.env.NODE_ENV === 'development' && authorizationError === undefined) {
    return <DevelopmentError errors={errors} />
  }

  return (
    <CardContainer withLogo={false}>
      {waitingVerificationError === undefined ? (
        // eslint-disable-next-line sonarjs/no-nested-conditional
        authorizationError === undefined ? (
          // eslint-disable-next-line sonarjs/no-nested-conditional
          notFoundError === undefined ? (
            <StandardError errors={errors} />
          ) : (
            <NotFoundError />
          )
        ) : (
          <UnauthorizedError error={authorizationError} />
        )
      ) : (
        <WaitingVerification />
      )}
    </CardContainer>
  )
}

// 各所で適切なハンドリングをしたつもりだが、無駄に何度もレンダリングが走り、見た目がちらついたりする
// 下記も不要なはずだが、一応いれておく
// export const Error = React.memo(ErrorRaw, (prev, next) => {
//   return JSON.stringify(prev) === JSON.stringify(next)
// })
